import type { JsonValue } from '../components/JsonTreeEditor';
import { pathToKey, type JsonPath } from './jsonPath';

export interface JsonTextMap {
	text: string;
	lineOffsets: number[];
	pathLineMap: Record<string, number>;
}

interface SerializeState {
	lineCount: number;
	pathLineMap: Record<string, number>;
}

function stringifyPrimitive(value: JsonValue) {
	if (value === null) {
		return 'null';
	}

	if (typeof value === 'string') {
		return JSON.stringify(value);
	}

	return String(value);
}

function appendComma(lines: string[]) {
	if (lines.length > 0) {
		lines[lines.length - 1] = `${lines[lines.length - 1]},`;
	}
}

function serializeNode(
	value: JsonValue,
	path: JsonPath,
	indentLevel: number,
	state: SerializeState,
	prefix = '',
): string[] {
	const indent = ' '.repeat(indentLevel * 4);
	const currentKey = pathToKey(path);
	state.pathLineMap[currentKey] = state.lineCount;

	if (Array.isArray(value)) {
		if (value.length === 0) {
			state.lineCount += 1;
			return [`${indent}${prefix}[]`];
		}

		const lines = [`${indent}${prefix}[`];
		state.lineCount += 1;
		value.forEach((item, index) => {
			const childLines = serializeNode(item, [...path, index], indentLevel + 1, state);
			if (index < value.length - 1) {
				appendComma(childLines);
			}
			lines.push(...childLines);
		});
		lines.push(`${indent}]`);
		state.lineCount += 1;
		return lines;
	}

	if (value !== null && typeof value === 'object') {
		const entries = Object.entries(value);

		if (entries.length === 0) {
			state.lineCount += 1;
			return [`${indent}${prefix}{}`];
		}

		const lines = [`${indent}${prefix}{`];
		state.lineCount += 1;
		entries.forEach(([childKey, childValue], index) => {
			const childLines = serializeNode(
				childValue,
				[...path, childKey],
				indentLevel + 1,
				state,
				`${JSON.stringify(childKey)}: `,
			);
			if (index < entries.length - 1) {
				appendComma(childLines);
			}
			lines.push(...childLines);
		});
		lines.push(`${indent}}`);
		state.lineCount += 1;
		return lines;
	}

	state.lineCount += 1;
	return [`${indent}${prefix}${stringifyPrimitive(value)}`];
}

export function formatJsonText(value: JsonValue): JsonTextMap {
	const state: SerializeState = {
		lineCount: 0,
		pathLineMap: {},
	};
	const lines = serializeNode(value, [], 0, state);
	const lineOffsets: number[] = [];

	let offset = 0;
	lines.forEach((line) => {
		lineOffsets.push(offset);
		offset += line.length + 1;
	});

	return {
		text: lines.join('\n'),
		lineOffsets,
		pathLineMap: state.pathLineMap,
	};
}
